import { useEffect, useRef, useState } from "react";
import { LabSignUpFormData, TApiAddress, TApiLab } from "../../../lib/types/labs";
import { getCacheKillQueryString } from "../../../lib/common/api";
import { useLocation, useParams } from "react-router-dom";
import { useAuthContext } from "../../../contexts/auth-context";
import { FileUploaderComponent, useFileManagement } from "../../../services/file-management";
import { useUserService } from "../../../services/users";
import { useLabs } from "../../../services/laboratories";
import { TProcessStates } from "../../../lib/types/general";
import { ErrorMessageSticky } from "../../common/alert-message";
import { useNotificationContext } from "../../../contexts/notification-context";
import { ConnectionRequestInfoAndButtons } from "../../common/notifications";
import { UserNotExist } from "../../common/user-not-exist";
import { BackButton } from "../../common/backbutton";

import { LeftMenu } from "../../common/menus/left-menu";
import { useRouteNavigation } from "../../../lib/common/routes";
import { LifeStyle } from "./lifestyle";
import { PartFirst, PartSecond } from "./about-our-area";
import { StripeComponent } from "./stripe-component";
import { evaluators, useWindowSizeMatch } from "../../../lib/window-size-matcher";
import { TMenuCustomCSSClasses, TMenuOption } from "../../common/menus/types";
import { HorizontalMenu } from "../../common/menus/horizontal-menu";
import { DEVICES_SIZES_IN_PX } from "../../../lib/common/constants";
import { useAddress } from "../../../services/regions";
import { OnboardingProcess } from "../../common/onboarding-process";
import { useOnboardingProcess } from "../../../lib/common/onboarding_steps";
import { ProfileDock } from "../../common/profile-dock";

export type TDentalLabInfoProps = {
    editable?: boolean;
    labExist: boolean;
    labInfo?: TApiLab;
};

export const LabInfo = ({ editable = false, labExist, labInfo }: TDentalLabInfoProps) => {
    const [backgroundImg, setBackgroundImg] = useState<string | undefined>(undefined);

    const urlInfo = useLocation();
    let pathname = urlInfo.pathname;
    if (urlInfo.pathname) {
        const pathParts = urlInfo.pathname.split("/");
        if (!isNaN(Number(pathParts[pathParts.length - 1]))) {
            pathParts.pop();
            pathname = pathParts.join("/");
        }
    }

    const [inappropriateContent, setInappropriateContent] = useState(false);
    const [unsupportedImageType, setUnsupportedImageType] = useState(false);
    const [uploadImageError, setUploadImageError] = useState("");
    const [isSomePartBeingEdited, setIsSomePartBeingEdited] = useState(false);

    const updateLabAndLocalStorage = async (data: LabSignUpFormData, name?: string) => {
        await updateLab(data, name);
        const dataForStorage = LabSignUpFormData2UserInfo(data, userInfo, name);
        updateUserInfoInStorage(dataForStorage);
        updateUserInfoInContext(dataForStorage); //TODO: review this, it generates an unwanted refresh same as in dental-tech-info
        window.wwgLabInfo_dataWasModified = true;
        coordinatesHandler(userInfo.id);
        refreshOnboardingProgress();
    };

    const updateBackground = (userGuid: string, newfile?: string, inappropriateContent?: boolean, unsupportedImageType?: boolean, errorMessage?: string) => {
        if (errorMessage) {
            window.scrollTo({ top: 0 });
            setUploadImageError(errorMessage);
            return;
        }
        if (inappropriateContent) {
            window.scrollTo({ top: 0 });
            setUploadImageError("");
            setInappropriateContent(true);
            setBackgroundImg(undefined);
        } else if (unsupportedImageType) {
            window.scrollTo({ top: 0 });
            setUploadImageError("");
            setUnsupportedImageType(true);
            setBackgroundImg(undefined);
        } else {
            if (!newfile) {
                setBackgroundImg(undefined);
            } else {
                const cacheKill = editable ? `?${getCacheKillQueryString()}` : "";
                setUploadImageError("");
                setInappropriateContent(false);
                setUnsupportedImageType(false);
                setBackgroundImg(getImagePath(newfile, "fixed", !editable ? userGuid : undefined) + cacheKill);
            }
        }
    };

    const { userId: userIdByParam } = useParams();
    const { userInfo: currentUserInfo, setUserInfo: updateUserInfoInContext, updateUserInfoFromLocalStorage, updateUserInfoInStorage } = useAuthContext();
    const { getImagePath, makeDroppableCapable } = useFileManagement();
    const isOwnProfile = !userIdByParam || Number(userIdByParam) === currentUserInfo?.id;
    const { updateLab, LabSignUpFormData2UserInfo } = useUserService();
    const {
        getLab,
        public: { getVerifyEmployerIsDuplicate },
    } = useLabs();
    const { getCoordinates } = useAddress();
    const { onboardingStatus, nextOnboardingStep, remainingSteps, refreshOnboardingProgress } = useOnboardingProcess();
    const [userLatitude, setUserLatitude] = useState(0);
    const [userLongitude, setUserLongitude] = useState(0);

    const [workingState, setWorkingState] = useState<TProcessStates>("working");
    const [userInfo, setUserInfo] = useState<TApiLab>({
        business_type_id: -1,
        business_type_name: "",
        id: -1,
        guid: "",
        name: "",
        inmycontactlist: false,
        invitationsenttome: false,
        is_in_blacklist: "",
        verifications: {
            phone: false,
            email: undefined,
        },
        other: {
            desiredRegion: [],
            desiredSalaryPerYear: { amount: 0, frequency: "yearly" },
            email: "",
            firstName: "",
            hobbies: [],
            imgUrl: "",
            interests: [],
            lastName: "",
            positions: [],
            yearsOfExperience: 0,
            philosophy: "",
            location: "",
            phone: "",
            website: "",
            size: "",
            specialty: "",
            history: "",
            description: "",
            zip: "",
            aboutArea: "",
            video: "",
            address: {
                street: "",
                street_number: "",
                city: "",
                state: "",
            },
            aboutRestaurants: [
                {
                    isSelected: false,
                    id: "",
                    name: "",
                    image_url: "",
                    url: "",
                    rating: 0,
                    categories: [],
                },
            ],
            aboutEvents: [
                {
                    isSelected: false,
                    id: "",
                    name: "",
                    url: "",
                    dates: {
                        start: {
                            localDate: "",
                        },
                    },
                    images: [],
                    classifications: [
                        {
                            segment: {
                                name: "",
                            },
                        },
                    ],
                },
            ],
            aboutLandmarks: [
                {
                    isSelected: false,
                    id: "",
                    name: "",
                    image_url: "",
                    url: "",
                    rating: 0,
                    location: {
                        address1: "",
                    },
                },
            ],
        },
        images: {
            background: "",
            external: "",
            internal: "",
            logo: "",
            resume: "",
        },
    });
    const [userExist, setUserExist] = useState<boolean>(true);
    const refProfileBackround = useRef<HTMLDivElement>(null);

    const coordinatesHandler = async (userId: number) => {
        const coordinates = await getCoordinates(userId);
        setUserLatitude(coordinates.data[0].lat);
        setUserLongitude(coordinates.data[0].long);
    };

    const bringUserInfo = async () => {
        if (!labExist) {
            setUserExist(false);
            return;
        }

        const info = labInfo !== undefined ? [labInfo] : await getLab(Number(userIdByParam) || (currentUserInfo?.id as number));
        if (info.length === 0) {
            setUserExist(false);
            return;
        }
        coordinatesHandler(info[0].id);
        setUserInfo(info[0]);
        updateBackground(info[0].guid, { ...info[0]?.images }?.background);

        setWorkingState("done");
        window.scrollTo({ top: 0, behavior: "smooth" });
        refreshOnboardingProgress();
        return info[0];
    };

    const validateDuplicateAddressAndName = async (name: string, address: TApiAddress, userId: number): Promise<boolean> => {
        const isAddressOrNameDuplicate = await getVerifyEmployerIsDuplicate(name, address, userId);
        if (isAddressOrNameDuplicate > 0) {
            return true;
        } else {
            return false;
        }
    };

    useEffect(() => {
        window.scrollTo({ top: 0 });
        bringUserInfo().then((_uInfo) => {
            if (editable && _uInfo) {
                makeDroppableCapable(
                    refProfileBackround,
                    "background",
                    "image",
                    "fixed",
                    (newfile, inappropriateContent, unsupportedImageType, errorMessage) => {
                        updateBackground(_uInfo.guid, newfile, inappropriateContent, unsupportedImageType, errorMessage);
                        refreshOnboardingProgress();
                    },
                );
            }
        });

        return () => {
            if (window.wwgLabInfo_dataWasModified) {
                window.wwgLabInfo_dataWasModified = false;
                updateUserInfoFromLocalStorage();
            }
        };
        //eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userIdByParam]);

    const InvitationButtons = () => {
        const { invitationsList } = useNotificationContext();
        const userInvitation = invitationsList.find((p) => p.from === userInfo.id);
        return (
            <div className="connectionRequestLabProfile">
                {userInfo.invitationsenttome && userInvitation && (
                    <ConnectionRequestInfoAndButtons
                        userId={userInfo.id}
                        wideStyle={true}
                        userName={userInfo.name}
                        invitationMessage={userInvitation.message}
                    />
                )}
            </div>
        );
    };

    const { getRoute, navigateToRoute } = useRouteNavigation();

    const options: TMenuOption[] = [
        {
            label: "My Profile",
            iconClass: "fa-solid fa-user",
            pathName: getRoute("dentalLabAccount"),
            clickHandler: () => navigateToRoute("dentalLabAccount"),
        },
        {
            label: "Subscription",
            iconClass: "fa-solid fa-credit-card",
            pathName: getRoute("dentalLabSubscription"),
            clickHandler: () => navigateToRoute("dentalLabSubscription"),
        },
    ];

    type TComponentMode = "myProfile" | "subscription";
    const localRoutes: Record<string, TComponentMode> = {
        [getRoute("dentalLabAccount")]: "myProfile",
        [getRoute("dentalLabInfo")]: "myProfile",
        [getRoute("dentalLabSubscription")]: "subscription",
    };

    const selectedMenuOption: TComponentMode = localRoutes[pathname];
    const customLeftMenuCSSClases: TMenuCustomCSSClasses = {
        active: "bg-light",
        menuList: "labAccountLeftMenuList",
        menuItem: "labAccountLeftMenuListItem",
    };
    const { conditionFulfilled: isSmallDevice } = useWindowSizeMatch({
        condition: evaluators.lessThan(DEVICES_SIZES_IN_PX.medium),
    });

    return (
        <>
            {!userExist && <UserNotExist />}
            {userExist && (
                <>
                    {isSmallDevice.fulfilled && currentUserInfo?.type === "lab" && (
                        <div className="container-fluid">
                            <HorizontalMenu optionList={options} />
                        </div>
                    )}
                    {/* Background Profile*/}
                    <span id="upload_background-progress"></span>
                    <div
                        className={"labprofilecontentblock container-fluid " + (workingState === "done" ? "" : "cssLoading")}
                        ref={refProfileBackround}
                        style={{
                            [backgroundImg ? "backgroundImage" : ""]: `url('${backgroundImg}')`,
                        }}
                    >
                        <div className="container">
                            {/*Row for Edit Bg button, backbutton and invitation notification */}
                            <div className="row pt-3">
                                {editable && !isSomePartBeingEdited && (
                                    <FileUploaderComponent
                                        relatedComponent={refProfileBackround}
                                        tooltipPosition="right"
                                        fileToolTipText="Click here to edit your background photo, or drag and drop it onto this area. Images should be in .JPG, .JPEG, .PNG, .HEIC or .HEIF formats. Your background image should be 1220x320 pixels."
                                    />
                                )}
                                {!editable && (
                                    <BackButton />
                                )}
                                {!document.querySelector(".cssLoading") && (uploadImageError || inappropriateContent || unsupportedImageType) && (
                                    <ErrorMessageSticky
                                        closeClickHandler={() => {
                                            setUploadImageError("");
                                            setInappropriateContent(false);
                                            setUnsupportedImageType(false);
                                        }}
                                        message={
                                            uploadImageError
                                                ? uploadImageError
                                                : inappropriateContent
                                                  ? "Image not accepted. It may contain inappropriate content. Please try again with a suitable image."
                                                  : unsupportedImageType
                                                    ? "Image format not accepted. Please use JPEG, JPG, PNG, HEIC, or HEIF formats."
                                                    : ""
                                        }
                                    />
                                )}
                                {!!nextOnboardingStep && editable && (
                                    <OnboardingProcess
                                        progress={onboardingStatus}
                                        nextStep={nextOnboardingStep}
                                        remainingSteps={remainingSteps}
                                        userType="lab"
                                    />
                                )}
                                <InvitationButtons />
                            </div>
                        </div>
                    </div>
                    <div className="container-fluid">
                        <div className="row m-sm-1">
                            {editable && (
                                <div className="col-lg-2">
                                    {!isSmallDevice.fulfilled && <LeftMenu optionList={options} customCSSClasses={customLeftMenuCSSClases} />}
                                </div>
                            )}
                            {selectedMenuOption === "myProfile" && (
                                <div className="col">
                                    <PartFirst
                                        setInappropriateContent={setInappropriateContent}
                                        setUnsupportedImageType={setUnsupportedImageType}
                                        setUploadErrorMessage={setUploadImageError}
                                        userId={userIdByParam}
                                        editable={editable}
                                        isSomePartBeingEdited={isSomePartBeingEdited}
                                        setIsSomePartBeingEdited={setIsSomePartBeingEdited}
                                        setUserInfo={setUserInfo}
                                        updateLabAndLocalStorage={updateLabAndLocalStorage}
                                        userInfo={userInfo}
                                        workingState={workingState}
                                        userLatitude={userLatitude}
                                        userLongitude={userLongitude}
                                        coordinatesHandler={coordinatesHandler}
                                        verifyDuplicateNameAddress={validateDuplicateAddressAndName}
                                        onboardingStepCompleted={refreshOnboardingProgress}
                                    />
                                    {/* About the Area */}

                                    <PartSecond
                                        setInappropriateContent={setInappropriateContent}
                                        setUnsupportedImageType={setUnsupportedImageType}
                                        setUploadErrorMessage={setUploadImageError}
                                        userId={userIdByParam}
                                        editable={editable}
                                        isSomePartBeingEdited={isSomePartBeingEdited}
                                        setIsSomePartBeingEdited={setIsSomePartBeingEdited}
                                        setUserInfo={setUserInfo}
                                        updateLabAndLocalStorage={updateLabAndLocalStorage}
                                        userInfo={userInfo}
                                        workingState={workingState}
                                        userLatitude={userLatitude}
                                        userLongitude={userLongitude}
                                        coordinatesHandler={coordinatesHandler}
                                        verifyDuplicateNameAddress={validateDuplicateAddressAndName}
                                        onboardingStepCompleted={refreshOnboardingProgress}
                                    />
                                    <span id="choose_lifestyle-progress"></span>
                                    <LifeStyle
                                        editable={editable}
                                        isSomePartBeingEdited={isSomePartBeingEdited}
                                        setIsSomePartBeingEdited={setIsSomePartBeingEdited}
                                        setUserInfo={setUserInfo}
                                        updateLabAndLocalStorage={updateLabAndLocalStorage}
                                        userInfo={userInfo}
                                        workingState={workingState}
                                        userId={userIdByParam}
                                    />
                                </div>
                            )}
                            {selectedMenuOption === "subscription" && (
                                <div className="col">
                                    <StripeComponent />
                                </div>
                            )}
                        </div>
                        <div className="container"></div>
                    </div>
                    {isSmallDevice.fulfilled && isOwnProfile && (
                        <ProfileDock progress={onboardingStatus} nextStep={nextOnboardingStep ?? null} userType={currentUserInfo?.type ?? "lab"} />
                    )}
                </>
            )}
        </>
    );
};
